import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

interface State {
    bgColor: string;
    distance: number;
    intensity: number;
    blur: number;
    radius: number;
    inset: boolean;
    copied: boolean;
}

function adjustBrightness(hex: string, percent: number): string {
    const m = hex.replace('#', '').match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
    if (!m) return hex;
    let h = m[0];
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const v = parseInt(h, 16);
    const r = Math.max(0, Math.min(255, Math.round(((v >> 16) & 0xff) * (1 + percent / 100))));
    const g = Math.max(0, Math.min(255, Math.round(((v >> 8) & 0xff) * (1 + percent / 100))));
    const b = Math.max(0, Math.min(255, Math.round((v & 0xff) * (1 + percent / 100))));
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

export default class NeumorphismGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        bgColor: '#e0e5ec',
        distance: 12,
        intensity: 15,
        blur: 24,
        radius: 16,
        inset: false,
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get cssCode(): string {
        const { bgColor, distance, intensity, blur, radius, inset } = this.state;
        const dark = adjustBrightness(bgColor, -intensity);
        const light = adjustBrightness(bgColor, intensity);
        const i = inset ? 'inset ' : '';
        return `background: ${bgColor};\nborder-radius: ${radius}px;\nbox-shadow: ${i}${distance}px ${distance}px ${blur}px ${dark},\n            ${i}${-distance}px ${-distance}px ${blur}px ${light};`;
    }

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { bgColor, distance, intensity, blur, radius, inset, copied } = this.state;
        const dark = adjustBrightness(bgColor, -intensity);
        const light = adjustBrightness(bgColor, intensity);
        const i = inset ? 'inset ' : '';
        const previewStyle: React.CSSProperties = {
            background: bgColor,
            borderRadius: `${radius}px`,
            boxShadow: `${i}${distance}px ${distance}px ${blur}px ${dark}, ${i}${-distance}px ${-distance}px ${blur}px ${light}`,
            width: 180,
            height: 180,
        };

        const slider = (label: string, value: number, min: number, max: number, fmt: (v: number) => string, onChange: (v: number) => void) => (
            <div className={genStyles.field}>
                <label>{label}: <strong>{fmt(value)}</strong></label>
                <input type="range" min={min} max={max} value={value} onChange={e => onChange(+e.target.value)} />
            </div>
        );

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Background</span></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input type="color" value={bgColor} style={{ width: 44, height: 38, border: '1px solid #e2e8f0', borderRadius: 6 }}
                            onChange={e => this.setState({ bgColor: e.target.value })} />
                        <input type="text" value={bgColor}
                            style={{ flex: 1, padding: '0.55rem 0.75rem', borderRadius: 6, border: '1px solid #e2e8f0', fontFamily: 'ui-monospace, monospace', fontSize: '0.9rem' }}
                            onChange={e => this.setState({ bgColor: e.target.value })} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Shadow</span></div>
                    {slider('Distance', distance, 2, 50, v => `${v}px`, v => this.setState({ distance: v }))}
                    {slider('Intensity', intensity, 5, 50, v => `${v}%`, v => this.setState({ intensity: v }))}
                    {slider('Blur', blur, 5, 100, v => `${v}px`, v => this.setState({ blur: v }))}
                    {slider('Radius', radius, 0, 50, v => `${v}px`, v => this.setState({ radius: v }))}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={inset}
                            onChange={e => this.setState({ inset: e.target.checked })} />
                        Inset (pressed style)
                    </label>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                preview={
                    <div style={{ background: bgColor, padding: 32, borderRadius: 8, display: 'flex', justifyContent: 'center' }}>
                        <div style={previewStyle} />
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
